import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck, Sparkles } from "lucide-react";

const INDUSTRIES = [
  "Medical / Dental",
  "Med Spa / Aesthetics",
  "Retail / Florist",
  "Auto / Mechanic",
  "Home Services (HVAC / Plumbing / Roofing)",
  "Legal / Accounting",
  "Salon / Barber",
  "Fitness / Wellness",
  "Other service business",
];

const TICKETS = [
  "Under $100",
  "$100 – $500",
  "$500 – $2,000",
  "$2,000 – $10,000",
  "$10,000+",
];

export default function AuditForm() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", business_name: "", website: "", phone: "",
    industry: INDUSTRIES[0], avg_ticket: TICKETS[2], notes: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // basic validation
    for (const k of ["name", "email", "business_name", "website", "phone"]) {
      if (!form[k] || !form[k].trim()) {
        toast.error("Please fill out every field — we use all of it for the audit.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await api.post("/audit/generate", form);
      toast.success("Audit started — generating now.");
      navigate(`/audit/result/${res.data.audit_id}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again in a sec.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grain min-h-screen flex flex-col">
      <Nav />
      <main className="flex-1 py-16 lg:py-24">
        <div className="max-w-[1080px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-5">
            <span className="tag mb-6"><span className="w-1.5 h-1.5 rounded-full bg-rust pulse-dot"></span>Free AI revenue leak audit</span>
            <h1 className="display text-5xl lg:text-6xl mt-6 leading-[1]">
              Tell us about your shop.<br />
              <span className="display-italic text-rust">We'll find the leak.</span>
            </h1>
            <p className="text-ink/70 mt-6 max-w-[40ch]">
              In under 60 seconds, our AI generates a custom audit of where your business is losing revenue — missed calls, weak GBP, slow follow-up, dead DMs. No pitch. Just numbers.
            </p>

            <ul className="mt-10 space-y-4 text-sm">
              <li className="flex items-start gap-3"><Sparkles size={18} className="text-rust mt-0.5" /><span>Audit ready in ~30 seconds, powered by Claude Sonnet 4.5.</span></li>
              <li className="flex items-start gap-3"><ShieldCheck size={18} className="text-rust mt-0.5" /><span>Your data is private. We don't sell, share, or spam.</span></li>
            </ul>

            <div className="mt-10 card-ink rounded-2xl p-6">
              <div className="mono text-[10px] uppercase tracking-wider text-cream/60">Sample insight</div>
              <p className="text-cream mt-3 leading-snug">
                "Riverbend Dental is leaking <span className="text-ember display-italic">~$8,400/mo</span> on after-hours calls and a Google profile sitting at 3.6★ with no posts in 90 days."
              </p>
            </div>
          </aside>

          <form onSubmit={submit} className="lg:col-span-7 card-paper rounded-2xl p-8 lg:p-10" data-testid="audit-form">
            <div className="mono text-xs uppercase tracking-[0.2em] text-mute mb-6">Step 01 — your shop</div>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
              <Field label="Your name">
                <input className="form-input" placeholder="Jane Smith" value={form.name} onChange={update("name")} data-testid="audit-input-name" required />
              </Field>
              <Field label="Email">
                <input type="email" className="form-input" placeholder="jane@yourshop.com" value={form.email} onChange={update("email")} data-testid="audit-input-email" required />
              </Field>
              <Field label="Business name">
                <input className="form-input" placeholder="Riverbend Dental" value={form.business_name} onChange={update("business_name")} data-testid="audit-input-business" required />
              </Field>
              <Field label="Website">
                <input className="form-input" placeholder="https://riverbenddental.com" value={form.website} onChange={update("website")} data-testid="audit-input-website" required />
              </Field>
              <Field label="Phone">
                <input className="form-input" placeholder="(555) 010-1234" value={form.phone} onChange={update("phone")} data-testid="audit-input-phone" required />
              </Field>
              <Field label="Industry">
                <select className="form-input" value={form.industry} onChange={update("industry")} data-testid="audit-input-industry">
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </Field>
              <Field label="Average ticket value">
                <select className="form-input" value={form.avg_ticket} onChange={update("avg_ticket")} data-testid="audit-input-ticket">
                  {TICKETS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </Field>
              <Field label="What's your biggest growth headache? (optional)" full>
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="Missed calls after-hours, slow follow-up, no reviews coming in…"
                  value={form.notes}
                  onChange={update("notes")}
                  data-testid="audit-input-notes"
                />
              </Field>
            </div>

            <div className="mt-10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-mute max-w-[36ch]">
                By requesting an audit, you agree to receive a one-time email with your results. No spam.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary px-7 py-4 rounded-full text-sm font-medium tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="audit-submit"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Generating audit…</> : <>Run my free audit <ArrowRight size={14} /></>}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="form-label">{label}</span>
      {children}
    </label>
  );
}
